import React, { useState } from "react";
import { Card, Collapse, Tag, theme } from "antd";
import { RightOutlined } from "@ant-design/icons";
import { JobCostingSummary } from "../types/costing.types";
import { ShipmentMetricStrip } from "./ShipmentDetailLayout";

interface FinancialSummaryCardProps { summary: JobCostingSummary | null; loading?: boolean; currencySymbol?: string; }

const FinancialSummaryCard: React.FC<FinancialSummaryCardProps> = ({ summary, loading = false, currencySymbol = "" }) => {
  const [active, setActive] = useState<string | string[]>([]);
  const { token } = theme.useToken();
  if (!summary) return <Card loading={loading}><span style={{ color: token.colorTextSecondary }}>Aucune donnée financière disponible</span></Card>;
  const formatNumber = (value: number | string | null) => {
    const n = value === null || value === undefined ? 0 : typeof value === "string" ? parseFloat(value) : value;
    return (Number.isNaN(n) ? 0 : n).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };
  const amount = (value: number) => `${formatNumber(value)}${currencySymbol ? ` ${currencySymbol}` : ""}`;
  const items = [];
  if (summary.lines_by_account_type?.length) items.push({
    key: "account_types", label: "Détail par type de compte",
    children: <div className="shipment-account-types">{summary.lines_by_account_type.map(item =>
      <div key={item.account_type_code}><Tag color={item.account_type_code === "INCOME" ? "green" : item.account_type_code === "EXPENSE" ? "orange" : "blue"}>{item.account_type_name}</Tag>
        <strong>{amount(item.total_amount)}</strong><span className="shipment-muted">{item.count} ligne{item.count > 1 ? "s" : ""}</span></div>)}</div>,
  });
  if (summary.invoiced_lines_count !== undefined || summary.uninvoiced_lines_count !== undefined) items.push({
    key: "billing_status", label: "Statut de facturation",
    children: <ShipmentMetricStrip items={[
      { label: "Lignes facturées", value: summary.invoiced_lines_count || 0, tone: "positive" },
      { label: "Lignes non facturées", value: summary.uninvoiced_lines_count || 0, tone: "warning" },
      ...(summary.total_invoiced_amount !== undefined ? [{ label: "Montant facturé", value: amount(summary.total_invoiced_amount) }] : []),
    ]} />,
  });
  return <div className="shipment-costing-summary">
    <ShipmentMetricStrip items={[
      { label: "Coûts totaux", value: amount(summary.total_cost) },
      { label: "Revenus totaux", value: amount(summary.total_revenue) },
      { label: "Marge bénéficiaire", value: amount(summary.profit_margin), tone: summary.profit_margin >= 0 ? "positive" : "negative" },
      { label: "Taux de marge", value: `${summary.profit_margin_percentage.toFixed(1).replace(".", ",")} %`, tone: summary.profit_margin >= 0 ? "positive" : "negative" },
    ]} />
    {items.length > 0 && <Collapse expandIcon={({ isActive }) => <RightOutlined rotate={isActive ? 90 : 0} aria-hidden="true" />} className="shipment-costing-details" ghost activeKey={active} onChange={setActive} items={[{ key: "details", label: "Détail par type de compte et facturation", children: <div>{items.map(item => <section key={item.key}><h3>{item.label}</h3>{item.children}</section>)}</div> }]} />}
  </div>;
};
export default FinancialSummaryCard;
